/**
 * The intro screen as a member sees it, drawn small (spec 2026-10-23 §4.1).
 *
 * The intro is a designed screen, not a full-bleed video: a branded
 * background, a rounded VIDEO CARD that takes the video's own shape inside
 * (frame width − 32) × (≤ 55 % of the frame height), and the controls OUTSIDE
 * the card so nothing covers the words in the video. lib/card-size.ts holds
 * the rule; the phone uses the same one.
 *
 * The poster comes from the server conversion and shows first; the video
 * loads only when the admin presses play (preload="none"), muted, as on the
 * phone. The Skip chip and "Tap for sound" are drawings, hidden from screen
 * readers: they are not controls here.
 */
import { cardSize } from '../lib/card-size';
import type { FinalizedMedia } from '../lib/intro-state';

/** The SCREEN inside the frame; the card rule is measured against it, as on the phone. */
const SCREEN_WIDTH = 248;
/** A modern phone screen is about 9 : 19.5. */
const SCREEN_HEIGHT = Math.round((SCREEN_WIDTH * 19.5) / 9);
const BEZEL = 6;

interface Props {
  media: FinalizedMedia | null;
  /** "English" / "Spanish" — names the video for screen readers. */
  languageName: string;
  /** What the card says when there is no video. */
  emptyText: string;
}

export function PhoneFramePreview({ media, languageName, emptyText }: Props) {
  const card = cardSize({
    frameWidth: SCREEN_WIDTH,
    frameHeight: SCREEN_HEIGHT,
    videoWidth: media?.width ?? 0,
    videoHeight: media?.height ?? 0,
  });

  return (
    <div
      className="relative mx-auto flex flex-col items-center overflow-hidden rounded-[2.25rem] border-slate-800 bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 dark:border-slate-600"
      style={{ width: SCREEN_WIDTH + 2 * BEZEL, height: SCREEN_HEIGHT + 2 * BEZEL, borderWidth: BEZEL }}
    >
      <div aria-hidden="true" className="flex w-full justify-end px-4 pt-5">
        <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white">Skip in 5</span>
      </div>

      <div className="flex flex-1 items-center justify-center">
        <div
          className="overflow-hidden rounded-2xl bg-black shadow-lg ring-1 ring-white/10"
          style={{ width: card.width, height: card.height }}
        >
          {media ? (
            <video
              key={media.url}
              src={media.url}
              poster={media.poster_url}
              controls
              muted
              playsInline
              preload="none"
              aria-label={`${languageName} intro video`}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center p-4 text-center text-xs text-slate-300">
              {emptyText}
            </div>
          )}
        </div>
      </div>

      <div aria-hidden="true" className="flex w-full items-center justify-between px-4 pb-6">
        <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white">Tap for sound</span>
        <span className="h-1 w-20 rounded-full bg-white/25" />
      </div>
    </div>
  );
}
